import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { TripList } from '@/components/profile/TripList';
import { useAuth } from '@/hooks/useAuth';
import ChildrenSilhouette from '@/components/shared/ChildrenSilhouette';
import { Settings, LogOut, Edit, Camera, ChevronRight, User } from 'lucide-react';
import LoginPrompt from '@/components/shared/LoginPrompt';
import OceanBackground from '@/components/background/OceanBackground';

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #162544 0%, #1e3860 40%, #162544 100%)' }}>
        <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'rgba(106,179,214,0.3)', borderTopColor: 'var(--bloom)' }} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen relative" style={{ background: 'linear-gradient(180deg, #162544 0%, #1e3860 40%, #162544 100%)' }}>
        <Navbar />
        <OceanBackground />
        <div className="pt-28 pb-20 relative z-10">
          <LoginPrompt title="个人中心" description="登录后管理你的行程、日记与账号设置" />
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    if (!window.confirm('确定要退出登录吗？')) return;
    setLoggingOut(true);
    try {
      await logout();
      navigate('/'); // 退出后回到首页，不再留在登录页
    } catch {
      alert('退出失败，请重试');
    } finally {
      setLoggingOut(false);
    }
  };

  const avatarUrl = user?.user_metadata?.avatar_url || user?.user_metadata?.picture || '';
  const displayName = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.user_metadata?.user_name || user?.email?.split('@')[0] || '旅行者';

  return (
    <div className="min-h-screen relative" style={{ background: 'linear-gradient(180deg, #162544 0%, #1e3860 40%, #162544 100%)' }}>
      <Navbar />
      <OceanBackground />
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-24 pb-16 relative z-10">
        {/* Profile header card */}
        <div className="frost overflow-hidden mb-8" style={{ animation: 'rise .75s ease-out both' }}>
          {/* Banner */}
          <div className="h-48 relative" style={{ background: 'linear-gradient(135deg, #1e3860 0%, #2d5082 50%, #3d82b0 100%)' }}>
            <div className="absolute inset-0 opacity-40">
              <ChildrenSilhouette className="w-full h-full" style={{ opacity: 0.5 }} />
            </div>
            <div className="absolute bottom-4 right-4 flex gap-2">
              <button onClick={() => navigate('/settings')} className="btn-ghost text-xs py-2 px-4 flex items-center gap-1.5">
                <Camera className="w-4 h-4" /> 编辑封面
              </button>
            </div>
          </div>

          <div className="px-6 md:px-8 pb-6 -mt-12 relative">
            <div className="flex flex-col md:flex-row items-start md:items-end gap-4">
              {/* Avatar */}
              <div className="w-24 h-24 rounded-2xl overflow-hidden flex items-center justify-center text-3xl font-bold text-white" style={{ background: avatarUrl ? 'transparent' : 'linear-gradient(135deg, var(--amber), #c49a3e)', boxShadow: '0 6px 20px rgba(212,168,83,0.4)' }}>
                {avatarUrl ? (
                  <img src={avatarUrl} alt="用户头像" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold cb">{displayName}</h1>
                <p className="text-sm cd mt-1">{user?.email || ''}</p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <span className="ls ls-tl">🐋 {user?.user_metadata?.level || '深海探险家'}</span>
                  <span className="ls ls-au">⭐ {user?.user_metadata?.points || 0} 积分</span>
                </div>
              </div>
              <div className="flex gap-2 self-start md:self-end">
                <button onClick={() => navigate('/settings')} className="btn-ghost text-sm flex items-center gap-1.5">
                  <Settings className="w-4 h-4" /> 设置
                </button>
                <button onClick={handleLogout} disabled={loggingOut} className="btd bt text-sm flex items-center gap-1.5">
                  <LogOut className="w-4 h-4" /> {loggingOut ? '退出中...' : '退出登录'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { num: '12', label: '旅行笔记', color: 'cg' },
            { num: '34', label: '去过城市', color: 'cs' },
            { num: '6', label: '场景收藏', color: 'ca' },
            { num: '256', label: '获得积分', color: 'cl' },
          ].map((s, i) => (
            <div key={i} className="frost p-5 text-center" style={{ animation: `rise .75s ease-out ${i * 0.08}s both` }}>
              <div className={`text-3xl font-bold ${s.color} mb-0.5`}>{s.num}</div>
              <div className="text-[12px] cd">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Menu list */}
        <div className="frost overflow-hidden mb-8" style={{ animation: 'rise .75s ease-out 0.3s both' }}>
          {[
            { icon: <Edit className="w-4 h-4" />, label: '编辑个人信息', desc: '用户名、头像、个人签名' },
            { icon: <Settings className="w-4 h-4" />, label: '账号安全', desc: '修改密码、绑定邮箱' },
          ].map((item, i) => (
            <button key={i} className="w-full flex items-center justify-between p-4 hover:bg-[rgba(106,179,214,0.06)] transition text-left" style={{ borderBottom: i === 0 ? '1px solid rgba(156,200,224,0.08)' : 'none' }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(61,130,176,0.12)' }}>
                  {item.icon}
                </div>
                <div>
                  <div className="text-sm font-medium cb">{item.label}</div>
                  <div className="text-[11px] cd">{item.desc}</div>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 cd" />
            </button>
          ))}
        </div>

        {/* My trips */}
        <div style={{ animation: 'rise .75s ease-out 0.4s both' }}>
          <h3 className="text-xl font-bold cb mb-4">我的旅行</h3>
          <TripList userId={user?.id} />
        </div>
      </div>
    </div>
  );
};

export default Profile;
